import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
  Button,
  Switch,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PercentIcon from '@mui/icons-material/Percent';

const TaxList = ({ onEdit, onAdd }) => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadTaxes();
  }, []);

  const loadTaxes = () => {
    setLoading(true);
    axios.get('/api/taxes')
      .then(res => setTaxes(res.data.data || res.data))
      .catch(err => console.error('Error loading taxes:', err))
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(`/api/taxes/${id}/status`, { status: newStatus });
      setTaxes(taxes.map(tax => tax.id === id ? { ...tax, status: newStatus ? 1 : 0 } : tax));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update tax status');
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await axios.delete(`/api/taxes/${id}`);
        setTaxes(taxes.filter(tax => tax.id !== id));
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete tax');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredTaxes = taxes.filter(tax =>
    tax.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTaxes = filteredTaxes.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredTaxes.length / itemsPerPage);

  return (
    <Box>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: '#424242',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.875rem'
          }}
        >
          <PercentIcon sx={{ fontSize: 18 }} />
          All Taxes ({filteredTaxes.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              fontSize: '0.8rem',
              width: '180px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                }
              }
            }}
          />
          <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ 
            bgcolor: '#43a047',
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { 
              bgcolor: '#388e3c'
            }
          }}
        >
          Add Tax
        </Button>
        </Box>
      </Box>

      {taxes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <Typography color="text.secondary">
            No taxes found. Create your first tax above.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f5f5f5'
                }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Percentage</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTaxes.map((tax) => (
                  <TableRow 
                    key={tax.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'action.hover'
                      },
                      '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1.5,
                        fontSize: '0.8rem'
                      }
                    }}
                  >
                    <TableCell>{tax.id}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {tax.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: '#43a047', fontSize: '0.8rem' }}>
                        {tax.percentage}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(tax.status)}
                        onChange={(e) => handleStatusChange(tax.id, e.target.checked)}
                      color="secondary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        onClick={() => onEdit(tax)}
                        size="small"
                        sx={{ 
                        color: '#1976d2',
                        '&:hover': { 
                          bgcolor: '#e3f2fd'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(tax.id, tax.title)}
                        size="small"
                        sx={{ 
                          color: '#d32f2f',
                          ml: 1,
                          '&:hover': { 
                            bgcolor: '#ffebee'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TaxList;
